import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Truck, 
  Plus, 
  RefreshCw, 
  Clock, 
  ChefHat, 
  Package, 
  Bike, 
  CheckCircle,
  AlertTriangle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useDeliveryOrders, DeliveryOrderWithDetails } from "@/hooks/useDeliveryOrders";
import { DeliveryOrderCard } from "@/components/delivery/DeliveryOrderCard";
import { DeliveryConfirmationDialog } from "@/components/delivery/DeliveryConfirmationDialog";
import { cn } from "@/lib/utils";

interface KanbanColumnProps {
  title: string;
  icon: React.ReactNode;
  orders: DeliveryOrderWithDetails[];
  count: number;
  colorClass: string;
  onConfirm?: (orderId: string) => void;
  onMarkReady?: (orderId: string) => void;
  onSendForDelivery?: (orderId: string) => void;
  onConfirmDelivery?: (order: DeliveryOrderWithDetails) => void;
  onCancel?: (orderId: string) => void;
  isUpdating?: boolean;
}

function KanbanColumn({
  title,
  icon,
  orders,
  count,
  colorClass,
  onConfirm,
  onMarkReady,
  onSendForDelivery,
  onConfirmDelivery,
  onCancel,
  isUpdating,
}: KanbanColumnProps) {
  return (
    <div className="flex flex-col bg-muted/30 rounded-lg border min-w-[280px] max-w-[320px] flex-1">
      {/* Column Header */}
      <div className={cn("p-3 border-b rounded-t-lg", colorClass)}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {icon}
            <span className="font-semibold">{title}</span>
          </div>
          <Badge variant="secondary" className="font-bold">
            {count}
          </Badge>
        </div>
      </div>

      {/* Column Content */}
      <ScrollArea className="flex-1 p-2">
        <div className="space-y-2">
          {orders.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground text-sm">
              Nenhum pedido
            </div>
          ) : (
            orders.map((order) => (
              <DeliveryOrderCard
                key={order.id}
                order={order}
                onConfirm={onConfirm ? () => onConfirm(order.id) : undefined}
                onMarkReady={onMarkReady ? () => onMarkReady(order.id) : undefined}
                onSendForDelivery={onSendForDelivery ? () => onSendForDelivery(order.id) : undefined}
                onConfirmDelivery={onConfirmDelivery ? () => onConfirmDelivery(order) : undefined}
                onCancel={onCancel ? () => onCancel(order.id) : undefined}
                isUpdating={isUpdating}
              />
            ))
          )}
        </div>
      </ScrollArea>
    </div>
  );
}

export default function DeliveryPage() {
  const navigate = useNavigate();
  const {
    ordersByStatus,
    isLoading,
    refetch,
    confirmOrder,
    markReady,
    sendForDelivery,
    confirmDelivery,
    cancelOrder,
    isUpdating,
  } = useDeliveryOrders();

  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<DeliveryOrderWithDetails | null>(null);

  const handleConfirmDeliveryClick = (order: DeliveryOrderWithDetails) => {
    setSelectedOrder(order);
    setConfirmationDialogOpen(true);
  };

  const handleConfirmDelivery = (data: {
    orderId: string;
    paymentMethodId?: string;
    paymentAmount?: number;
    alreadyPaid: boolean;
  }) => {
    confirmDelivery(data);
    setSelectedOrder(null);
  };

  const handleCancelOrder = (orderId: string) => {
    const reason = window.prompt("Motivo do cancelamento (opcional):");
    if (reason !== null) {
      cancelOrder({ orderId, reason: reason || undefined });
    }
  };

  const totalActive =
    ordersByStatus.pending.length +
    ordersByStatus.preparing.length +
    ordersByStatus.ready.length +
    ordersByStatus.out_for_delivery.length;

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)] overflow-hidden">
      {/* Header */}
      <div className="border-b bg-card p-4 shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Truck className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h1 className="text-xl font-bold">Delivery</h1>
              <p className="text-sm text-muted-foreground">
                {totalActive} pedido(s) em andamento
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isLoading}>
              <RefreshCw className={cn("h-4 w-4 mr-2", isLoading && "animate-spin")} />
              Atualizar
            </Button>
            <Button onClick={() => navigate("/dashboard/pdv")} className="gap-2">
              <Plus className="h-4 w-4" />
              Novo Pedido
            </Button>
          </div>
        </div>
      </div>

      {/* Kanban Board */}
      <div className="flex-1 overflow-x-auto overflow-y-hidden p-4">
        <div className="flex gap-4 h-full min-w-max">
          {/* Pending Column */}
          <KanbanColumn
            title="Pendentes"
            icon={<Clock className="h-4 w-4" />}
            orders={ordersByStatus.pending}
            count={ordersByStatus.pending.length}
            colorClass="bg-amber-50 dark:bg-amber-950/30"
            onConfirm={confirmOrder}
            onCancel={handleCancelOrder}
            isUpdating={isUpdating}
          />

          {/* Preparing Column */}
          <KanbanColumn
            title="Em Preparo"
            icon={<ChefHat className="h-4 w-4" />}
            orders={ordersByStatus.preparing}
            count={ordersByStatus.preparing.length}
            colorClass="bg-blue-50 dark:bg-blue-950/30"
            onMarkReady={markReady}
            isUpdating={isUpdating}
          />

          {/* Ready Column */}
          <KanbanColumn
            title="Prontos"
            icon={<Package className="h-4 w-4" />}
            orders={ordersByStatus.ready}
            count={ordersByStatus.ready.length}
            colorClass="bg-green-50 dark:bg-green-950/30"
            onSendForDelivery={sendForDelivery}
            isUpdating={isUpdating}
          />

          {/* Out for Delivery Column */}
          <KanbanColumn
            title="Em Rota"
            icon={<Bike className="h-4 w-4" />}
            orders={ordersByStatus.out_for_delivery}
            count={ordersByStatus.out_for_delivery.length}
            colorClass="bg-purple-50 dark:bg-purple-950/30"
            onConfirmDelivery={handleConfirmDeliveryClick}
            isUpdating={isUpdating}
          />

          {/* Delivered Column */}
          <KanbanColumn
            title="Entregues"
            icon={<CheckCircle className="h-4 w-4" />}
            orders={ordersByStatus.delivered.slice(0, 10)}
            count={ordersByStatus.delivered.length}
            colorClass="bg-emerald-50 dark:bg-emerald-950/30"
            isUpdating={isUpdating}
          />
        </div>
      </div>

      {/* Empty state */}
      {totalActive === 0 && ordersByStatus.delivered.length === 0 && !isLoading && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="text-center">
            <Truck className="h-16 w-16 text-muted-foreground/30 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-muted-foreground">
              Nenhum pedido delivery
            </h3>
            <p className="text-sm text-muted-foreground/70">
              Pedidos do cardápio digital aparecerão aqui
            </p>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      <DeliveryConfirmationDialog
        open={confirmationDialogOpen}
        onOpenChange={setConfirmationDialogOpen}
        order={selectedOrder}
        onConfirm={handleConfirmDelivery}
        isLoading={isUpdating}
      />
    </div>
  );
}
